import {
  ClassDefinitionDto,
  ClassDefinitionItemDto,
  USState,
  UpsertClassDefinitionItemDto,
} from '@zorro/clients';
import {
  ALL_EMPLOYEES_CLASS_NAME,
  parseMaybeEnumValue,
  stateCodes,
} from '@zorro/common-utils';
import Papa from 'papaparse';

export type AllowanceModelClassDefinitions =
  | {
      isSuccess: true;
      message: string;
      classDefinitions: ClassDefinitionItemDto[];
    }
  | {
      isSuccess: false;
      message: string;
      classDefinitions?: undefined;
    };

export enum AllowanceModelClassPartType {
  State = 'State',
  RatingArea = 'RatingArea',
  EmploymentType = 'EmploymentType',
  WageType = 'WageType',
  Custom = 'Custom',
}

export function isCustomClassCategoryOnlyDefinition(
  classDefinition: ClassDefinitionItemDto
): boolean {
  return Boolean(
    classDefinition.customClassCategory &&
    !classDefinition.state &&
    !classDefinition.ratingArea &&
    !classDefinition.employmentType &&
    !classDefinition.wageType
  );
}

export type AllowanceModelClassPartTypeUnion =
  | AllowanceModelClassPartType.State
  | AllowanceModelClassPartType.RatingArea
  | AllowanceModelClassPartType.EmploymentType
  | AllowanceModelClassPartType.WageType
  | AllowanceModelClassPartType.Custom;

export type RequiredClassCategories = {
  hasWageType: boolean;
  hasEmploymentType: boolean;
  hasCustomClassCategory: boolean;
  hasState: boolean;
  hasRatingArea: boolean;
};

function normalizeClassPart(part: string): string {
  return part
    .trim()
    .replace(/[_\s-]+/gu, ' ')
    .toLowerCase();
}

function isEmploymentType(
  employmentTypeValue: string
): 'full time' | 'part time' | null {
  const normalizedValue = employmentTypeValue
    .replace(/[_\s-]/gu, '')
    .toLowerCase();
  if (normalizedValue.startsWith('full')) return 'full time';
  if (normalizedValue.startsWith('part')) return 'part time';
  return null;
}

function isWageType(wageTypeValue: string): 'salaried' | 'hourly' | null {
  const normalizedValue = wageTypeValue.replace(/[_\s-]/gu, '').toLowerCase();
  if (normalizedValue.startsWith('salar')) return 'salaried';
  if (normalizedValue.startsWith('hour')) return 'hourly';
  return null;
}

function isValidStateCode(code: string): boolean {
  return stateCodes.has(code.toUpperCase());
}

function isValidRatingArea(ratingAreaValue: string): boolean {
  return /^[a-z]{2} \d{1,2}$/u.test(ratingAreaValue);
}

function inferClassPartType(
  classPart: string
): AllowanceModelClassPartTypeUnion {
  const part = normalizeClassPart(classPart);
  if (isValidStateCode(part)) return AllowanceModelClassPartType.State;
  if (isValidRatingArea(part)) return AllowanceModelClassPartType.RatingArea;
  if (isEmploymentType(part)) return AllowanceModelClassPartType.EmploymentType;
  if (isWageType(part)) return AllowanceModelClassPartType.WageType;

  return AllowanceModelClassPartType.Custom;
}

function isValidateClassPartByType(
  part: string,
  type: AllowanceModelClassPartType
): boolean {
  const normalizedPart = normalizeClassPart(part);
  switch (type) {
    case AllowanceModelClassPartType.State:
      if (!isValidStateCode(normalizedPart)) {
        return false;
      }
      break;
    case AllowanceModelClassPartType.RatingArea:
      if (!isValidRatingArea(normalizedPart)) {
        return false;
      }
      break;
    case AllowanceModelClassPartType.EmploymentType:
      if (!isEmploymentType(normalizedPart)) {
        return false;
      }
      break;
    case AllowanceModelClassPartType.WageType:
      if (!isWageType(normalizedPart)) {
        return false;
      }
      break;
    case AllowanceModelClassPartType.Custom:
      if (!normalizedPart) {
        return false;
      }
      break;
  }
  return true;
}

function validateClassPartsAgainstFormat(
  classParts: string[],
  inferredTypes: AllowanceModelClassPartType[],
  rowNum: number,
  allClassesFromAllowanceModel: string[]
): string | undefined {
  if (classParts.length !== inferredTypes.length) {
    if (classParts.length === 1) {
      const singlePart = classParts[0].trim();
      const partType = inferClassPartType(singlePart);

      if (partType === AllowanceModelClassPartType.Custom) {
        const normalizedSinglePart = normalizeClassPart(singlePart);
        const isUniqueCustomCategory = allClassesFromAllowanceModel.every(
          (classString) => {
            const parts = classString
              .split(',')
              .map((part) => normalizeClassPart(part));
            if (parts.length === 1) {
              return true;
            }
            return !parts.includes(normalizedSinglePart);
          }
        );

        if (isUniqueCustomCategory) {
          return undefined;
        }
      }
    }

    return `Row ${rowNum}: class has ${classParts.length} parts, expected ${inferredTypes.length} (${inferredTypes.join(', ')})`;
  }
  for (let idx = 0; idx < classParts.length; idx++) {
    const part = normalizeClassPart(classParts[idx]);
    const type = inferredTypes[idx];
    const isValidPart = isValidateClassPartByType(part, type);

    if (!isValidPart) {
      return `Row ${rowNum}: class part "${part}" does not match expected type "${type}"`;
    }
  }
  return undefined;
}

function validateClassDefinitions(
  classDefinitions: UpsertClassDefinitionItemDto[]
): {
  isValid: boolean;
  error?: string;
} {
  if (classDefinitions.length === 0) {
    return {
      isValid: false,
      error: 'At least one class definition is required.',
    };
  }

  const customCategories = classDefinitions
    .map((def) => def.customClassCategory)
    .filter(Boolean);

  if (customCategories.length > 0) {
    const uniqueCustomCategories = new Set(customCategories);
    const hasOnlyOneValue = uniqueCustomCategories.size === 1;
    const isNotAllEmployees = !uniqueCustomCategories.has(
      ALL_EMPLOYEES_CLASS_NAME
    );

    if (hasOnlyOneValue && isNotAllEmployees) {
      return {
        isValid: false,
        error: 'Custom attributes must have at least 2 different values.',
      };
    }
  }

  const singleComponentCustomClasses = classDefinitions.filter(
    isCustomClassCategoryOnlyDefinition
  );

  for (const singleDef of singleComponentCustomClasses) {
    const singleCustomCategory = singleDef.customClassCategory;
    if (!singleCustomCategory) continue;

    const normalizedSingleCustomCategory =
      normalizeClassPart(singleCustomCategory);
    const isReusedInMultiComponent = classDefinitions.some((def) => {
      const customClassDefinition = def.customClassCategory
        ? normalizeClassPart(def.customClassCategory)
        : null;
      return (
        customClassDefinition === normalizedSingleCustomCategory &&
        (def.state || def.ratingArea || def.employmentType || def.wageType)
      );
    });

    if (isReusedInMultiComponent) {
      return {
        isValid: false,
        error: `Custom class category "${singleCustomCategory}" cannot be used both as a single-component class and in multi-component classes.`,
      };
    }
  }

  return { isValid: true };
}

export function extractAllowanceModelClassDefinitions(
  classesFromAllowanceModel: string[]
): AllowanceModelClassDefinitions {
  let classToUseForStructure = classesFromAllowanceModel[0];
  for (const classString of classesFromAllowanceModel) {
    const parts = classString.split(',');
    if (parts.length > 1) {
      classToUseForStructure = classString;
      break;
    }
  }

  const firstClass = classToUseForStructure.split(',');

  const inferredClassPartsTypes: AllowanceModelClassPartType[] = firstClass.map(
    (part) => inferClassPartType(part)
  );

  const uniqueTypes = new Set(inferredClassPartsTypes);
  if (uniqueTypes.size !== inferredClassPartsTypes.length) {
    return {
      isSuccess: false,
      message:
        'Error: Duplicate class part types detected. Each class part must have a unique type.',
    };
  }

  const errors: string[] = [];

  const classDefinitionsMap = new Map<string, UpsertClassDefinitionItemDto>();
  classesFromAllowanceModel.forEach((row: string, rowIndex) => {
    const rowNum = rowIndex + 2; // 1-based, plus header
    const classParts: string[] = row.split(',');
    const error = validateClassPartsAgainstFormat(
      classParts,
      inferredClassPartsTypes,
      rowNum,
      classesFromAllowanceModel
    );
    if (error) {
      errors.push(error);
    }

    const className = row.trim();
    if (classDefinitionsMap.has(className)) {
      return;
    }
    const classDefinition: UpsertClassDefinitionItemDto = { className };

    if (
      classParts.length === 1 &&
      classParts.length !== inferredClassPartsTypes.length
    ) {
      const singlePart = classParts[0].trim();
      const partType = inferClassPartType(singlePart);
      if (partType === AllowanceModelClassPartType.Custom) {
        classDefinition.customClassCategory = singlePart;
        classDefinitionsMap.set(className, classDefinition);
        return;
      }
    }

    classParts.forEach((part, idx) => {
      const type = inferredClassPartsTypes[idx];
      const value = part.trim();
      switch (type) {
        case AllowanceModelClassPartType.State: {
          const stateCode = parseMaybeEnumValue(USState, value.toUpperCase());
          if (!stateCode) {
            errors.push(
              `Row ${rowNum}: class part "${value}" is not a supported US state code`
            );
            break;
          }
          classDefinition.state = stateCode;
          break;
        }
        case AllowanceModelClassPartType.RatingArea:
          classDefinition.ratingArea = value;
          break;
        case AllowanceModelClassPartType.EmploymentType:
          if (isEmploymentType(value)) {
            classDefinition.employmentType =
              isEmploymentType(value) === 'full time'
                ? UpsertClassDefinitionItemDto.employmentType.FULL_TIME
                : UpsertClassDefinitionItemDto.employmentType.PART_TIME;
          }
          break;
        case AllowanceModelClassPartType.WageType:
          if (isWageType(value)) {
            classDefinition.wageType =
              isWageType(value) === 'salaried'
                ? UpsertClassDefinitionItemDto.wageType.SALARY
                : UpsertClassDefinitionItemDto.wageType.HOURLY;
          }
          break;
        case AllowanceModelClassPartType.Custom:
          classDefinition.customClassCategory = value;
          break;
      }
    });
    classDefinitionsMap.set(className, classDefinition);
  });
  const classDefinitions: UpsertClassDefinitionItemDto[] = Array.from(
    classDefinitionsMap.values()
  );

  const isSuccess = errors.length === 0;
  let message = '';

  if (isSuccess) {
    message = `Your Class Structure is Defined By: ${inferredClassPartsTypes.join(', ')}`;
  } else if (errors.length > 6) {
    const firstFiveErrors = errors
      .slice(0, 5)
      .map((error, index) => `${index + 1}. ${error}`)
      .join('\n');
    const remainingCount = errors.length - 5;
    message = `${firstFiveErrors}\n6. ${remainingCount} more errors`;
  } else {
    const numberedErrors = errors
      .map((error, index) => `${index + 1}. ${error}`)
      .join('\n');
    message = numberedErrors;
  }

  const validationResult = validateClassDefinitions(classDefinitions);
  if (!validationResult.isValid) {
    return {
      isSuccess: false,
      message: validationResult.error || 'Class definitions validation failed',
    };
  }

  if (!isSuccess) {
    return {
      isSuccess: false,
      message,
    };
  }

  return {
    isSuccess: true,
    message,
    classDefinitions,
  };
}

function parseAllowanceModel(csv: string): string[][] {
  const result = Papa.parse<string[]>(csv, { skipEmptyLines: true });
  if (result.errors.length > 0) {
    throw new Error(
      `CSV parse error: ${result.errors
        .map((parseError: Papa.ParseError) => parseError.message)
        .join('; ')}`
    );
  }
  return result.data as string[][];
}

export function getFieldRequirementsFromClassDefinitions(
  classDefinitions: UpsertClassDefinitionItemDto[]
): RequiredClassCategories {
  const hasWageType = classDefinitions.some((item) => Boolean(item.wageType));
  const hasEmploymentType = classDefinitions.some((item) =>
    Boolean(item.employmentType)
  );
  const hasCustomClassCategory = classDefinitions.some(
    (item) =>
      Boolean(item.customClassCategory) &&
      item.customClassCategory !== ALL_EMPLOYEES_CLASS_NAME
  );
  const hasState = classDefinitions.some((item) => Boolean(item.state));
  const hasRatingArea = classDefinitions.some((item) =>
    Boolean(item.ratingArea)
  );

  return {
    hasWageType,
    hasEmploymentType,
    hasCustomClassCategory,
    hasState,
    hasRatingArea,
  };
}

export function getRequiredClassCategoriesForTargetYear(
  classDefinitions: ClassDefinitionDto[],
  targetPlanYears: number[]
): RequiredClassCategories {
  const classDefinitionsForTargetYear = classDefinitions.filter(
    (classDefinition) => targetPlanYears.includes(classDefinition.year)
  );

  return getFieldRequirementsFromClassDefinitions(
    classDefinitionsForTargetYear.flatMap(
      (classDefinition) => classDefinition.items ?? []
    )
  );
}

export async function extractClassDefinitionsFromCsv(
  content: string
): Promise<AllowanceModelClassDefinitions> {
  const rows = parseAllowanceModel(content);
  if (rows.length < 2) {
    return {
      isSuccess: false,
      message: 'CSV must have a header and at least one data row.',
    };
  }
  const dataRows = rows.slice(1);
  const classIdx = 2;
  const classesFromAllowanceModel = dataRows.map((row) => row[classIdx]);

  return extractAllowanceModelClassDefinitions(classesFromAllowanceModel);
}

export function hasCustomClassCategories(customAttributes: string[]): boolean {
  const uniqueAttributes = Array.from(
    new Set(
      customAttributes
        .map((attribute) => attribute.trim())
        .filter((attribute) => attribute.length > 0)
    )
  );

  return uniqueAttributes.some(
    (attribute) => attribute !== ALL_EMPLOYEES_CLASS_NAME
  );
}
